import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellOff, BellRing, Send } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const isNativeApp =
  typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

const webSupported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/**
 * Renders the right push-notification opt-in for whichever platform the user
 * is running on:
 *   - Native (Capacitor Android/iOS) → FCM via @capacitor/push-notifications
 *   - Web (PWA, browser)             → VAPID web-push (existing flow)
 */
export function PushOptIn() {
  const { user } = useAuth();
  if (!user || !["shop", "owner"].includes(user.role)) return null;
  return isNativeApp ? <NativePushOptIn user={user} /> : <WebPushOptIn user={user} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Native (FCM) variant
// ─────────────────────────────────────────────────────────────────────────────
function NativePushOptIn({ user }) {
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState("prompt"); // "granted" | "denied" | "prompt"
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const perm = await PushNotifications.checkPermissions();
      setPermission(perm.receive);
      setEnabled(perm.receive === "granted");
    } catch {
      setPermission("denied");
      setEnabled(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const req = await PushNotifications.requestPermissions();
      setPermission(req.receive);
      if (req.receive !== "granted") {
        toast.error("Notifications blocked. Enable them in Settings → MechaniCar → Notifications.");
        return;
      }
      // Listen for the token registration (one-time)
      const tokenPromise = new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error("timeout")), 15000);
        PushNotifications.addListener("registration", async (tok) => {
          clearTimeout(t);
          resolve(tok?.value);
        });
        PushNotifications.addListener("registrationError", (err) => {
          clearTimeout(t);
          reject(err);
        });
      });
      await PushNotifications.register();
      const token = await tokenPromise;
      if (token) {
        await api.post("/push/fcm/register", { token, platform: "android" });
        setEnabled(true);
        toast.success("Notifications on — you're all set.");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("FCM enable failed:", e);
      toast.error(e?.message === "timeout"
        ? "Couldn't get an FCM token. Check that Google Play Services is installed."
        : "Couldn't enable notifications — try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/push/fcm/unregister", {});
      setEnabled(false);
      toast("Notifications off.");
    } catch {
      toast.error("Couldn't turn off notifications.");
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data?.delivered > 0) toast.success("Test push sent — watch for the notification.");
      else toast.error("Couldn't deliver — try toggling off & on, then retry.");
    } catch {
      toast.error("Couldn't send test notification.");
    } finally {
      setBusy(false);
    }
  }, []);

  const blocked = permission === "denied";
  return (
    <Shell
      enabled={enabled}
      busy={busy}
      onToggle={enabled ? disable : enable}
      onTest={sendTest}
      onTestDisabled={!enabled}
      role={user?.role}
      blocked={blocked}
      blockedMessage="Notifications blocked. Enable them in your phone's Settings → MechaniCar → Notifications."
      unsupportedMessage={null}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Web push (VAPID) variant — unchanged behaviour
// ─────────────────────────────────────────────────────────────────────────────
function WebPushOptIn({ user }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(webSupported ? Notification.permission : "denied");

  useEffect(() => {
    if (!webSupported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        if (!reg) { setEnabled(false); return; }
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub && Notification.permission === "granted");
        setPermission(Notification.permission);
      } catch { setEnabled(false); }
    })();
  }, [user]);

  const enable = useCallback(async () => {
    if (!webSupported) { toast.error("Push isn't supported in this browser."); return; }
    setBusy(true);
    try {
      const reg = (await navigator.serviceWorker.getRegistration("/sw.js")) || (await navigator.serviceWorker.register("/sw.js"));
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notifications were blocked. Enable them in your browser site settings, then try again.");
        return;
      }
      const { data } = await api.get("/push/vapid-public-key");
      const key = urlBase64ToUint8Array(data.key);
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
      setEnabled(true);
      toast.success("Notifications on — you'll get a ping the moment a new job lands.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error("Couldn't enable notifications. Try reloading the page first.");
    } finally { setBusy(false); }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      let endpoint = null;
      if (sub) { endpoint = sub.endpoint; try { await sub.unsubscribe(); } catch { /* noop */ } }
      await api.post("/push/unsubscribe", endpoint ? { endpoint } : {});
      setEnabled(false);
      toast("Notifications off.");
    } catch { toast.error("Couldn't turn off notifications."); }
    finally { setBusy(false); }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data?.delivered > 0) toast.success("Test push sent — watch for the notification.");
      else toast.error("Couldn't deliver — try toggling off & on, then retry.");
    } catch { toast.error("Couldn't send test notification."); }
    finally { setBusy(false); }
  }, []);

  return (
    <Shell
      enabled={enabled}
      busy={busy}
      onToggle={enabled ? disable : enable}
      onTest={sendTest}
      onTestDisabled={!enabled}
      role={user?.role}
      blocked={webSupported && permission === "denied"}
      blockedMessage="Notifications are blocked in this browser's site settings."
      unsupportedMessage={!webSupported ? "Your browser doesn't support push notifications." : null}
    />
  );
}

function Shell({ enabled, busy, onToggle, onTest, onTestDisabled, role, blocked, blockedMessage, unsupportedMessage }) {
  return (
    <div className="border border-white/10 bg-[#121214] p-5 sm:p-6" data-testid="push-opt-in">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
            {enabled ? <BellRing className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.2} /> : <Bell className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.2} />}
          </div>
          <div>
            <div className="label-eyebrow text-[#FF3B30] mb-1">PUSH NOTIFICATIONS</div>
            <h3 className="font-heading text-lg font-bold tracking-tight">
              {role === "owner" ? "Get notified the moment a new quote arrives" : "Get notified the moment a new job lands"}
            </h3>
            <p className="text-zinc-500 text-xs mt-1 max-w-md">
              {role === "owner"
                ? "We'll send a notification as soon as a shop sends a private quote on one of your jobs, or replies to your messages."
                : "We'll send a notification as soon as an owner posts a job in your radius. Works even with the app closed."}
            </p>
            {unsupportedMessage && (
              <p className="text-[#FF9500] text-xs mt-2 flex items-center gap-1">
                <BellOff className="w-3 h-3" /> {unsupportedMessage}
              </p>
            )}
            {blocked && (
              <p className="text-[#FF9500] text-xs mt-2 flex items-center gap-1">
                <BellOff className="w-3 h-3" /> {blockedMessage}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy || !!unsupportedMessage || blocked}
          className={`relative inline-flex h-7 w-14 shrink-0 items-center border transition-colors ${enabled ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-white/5 border-white/15"} disabled:opacity-40`}
          aria-pressed={enabled}
          aria-label={enabled ? "Disable push notifications" : "Enable push notifications"}
          data-testid="push-opt-in-toggle"
        >
          <span className={`inline-block h-5 w-5 bg-white transition-transform ${enabled ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </div>
      {enabled && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <button
            type="button"
            onClick={onTest}
            disabled={busy || onTestDisabled}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] disabled:opacity-50 transition-colors"
            data-testid="push-test-btn"
          >
            <Send className="w-3.5 h-3.5" /> Send test notification
          </button>
        </div>
      )}
    </div>
  );
}
